import { useNavigate } from 'react-router'
import Button from '@/components/ui/Button'
import Dialog from '@/components/ui/Dialog'
import { useBillingStore } from '@/store/billingStore'
import { useTranslation } from 'react-i18next'
import type { QuotaError } from '@/@types/trackheros'

/**
 * The button that will actually help (plans v2). Out of hours on a plan with
 * add-ons — "Add hours", not "Upgrade". Storage full — "Manage recordings"
 * first, because deleting old recordings is free.
 */
const action = (error: QuotaError) => {
    if (error.code === 'payment_method_required') {
        return { to: '/settings/billing', labelKey: 'billing.viewPlans' }
    }
    if (error.quota === 'meeting_minutes' && error.addon_available) {
        return {
            to: '/settings/billing#addons',
            labelKey: 'billing.quota.addHours',
        }
    }
    if (error.quota === 'recording_storage_hours') {
        return {
            to: '/settings/storage',
            labelKey: 'billing.relief.recordings',
        }
    }
    return { to: '/settings/billing#plans', labelKey: 'billing.quota.upgrade' }
}

/**
 * Explains a plan limit the API just refused (DEV-87).
 *
 * Mounted once at the top of the app and driven by the axios interceptor (and
 * by the chat stream, which does not go through axios), so every 402 gets an
 * explanation without each call site learning about plans.
 *
 * The interceptor still rejects the promise after publishing here — this is
 * deliberately *additional* to whatever the caller does with the error, not a
 * replacement for it. A form that shows the message inline next to the field
 * gives better feedback than a modal, and both can be true at once.
 */
const QuotaDialog = () => {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const quotaError = useBillingStore((state) => state.quotaError)
    const dismiss = useBillingStore((state) => state.dismissQuotaError)

    if (!quotaError) return null

    const next = action(quotaError)
    const go = () => {
        dismiss()
        navigate(next.to)
    }

    return (
        <Dialog isOpen width={480} onClose={dismiss} onRequestClose={dismiss}>
            <h5 className="mb-4">
                {quotaError.code === 'payment_method_required'
                    ? t('billing.quota.paymentMethodNeeded')
                    : quotaError.code === 'feature_not_in_plan'
                      ? t('billing.quota.notInPlan')
                      : t('billing.quota.limitReached')}
            </h5>
            <p className="text-gray-500 dark:text-gray-400">
                {quotaError.detail}
            </p>
            <div className="mt-6 flex justify-end gap-2">
                <Button size="sm" onClick={dismiss}>
                    {t('common.notNow')}
                </Button>
                <Button size="sm" variant="solid" onClick={go}>
                    {t(next.labelKey)}
                </Button>
            </div>
        </Dialog>
    )
}

export default QuotaDialog
